
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands } from "@/utils/common";

import { Table, TableRowsSelect } from "@/utils/decorator";
import t from "@common/src/i18n/t";
import Template from "@/views/dashboard/template.vue";

const setting = namespace("setting");

// 定义接口
interface MenuForm {
  menuName: string;
  menuIcon: string;
  resourceType: number;
  pageUrl: string;
  displayOrder: number;
  menuParent: string;
  menuParentName: string;
  menuCode: string;
  status: number;
  resourceCode: string;
  parentResourceCode: string;
  parentMenuCode: string;
  resourceClass: string;
  accessType: string;
}

interface TableScope {
  row: any;
  column: any;
  $index: number;
}

@Component({
  components: { Template }
})
@Table("loadListData")
@TableRowsSelect({
  warningMsg: t("setting.select-spare-parts"),
  updateStatusFuncName: "confirmUpdateStatus",
  tableRefKey: "table"
})
export default class PartsList extends Vue {
  @setting.Action getMenuList!: Function;
  @setting.Action saveMenuAndResource!: Function;
  @setting.Action delMenuAndResource!: Function;
  @setting.Action getMenuByCode!: Function;
  @setting.Action updateMenuAndResource!: Function;

  // 表格相关
  mix_loading: boolean = false;
  mix_pageNum: number = 1;
  mix_selectedRows: any[] = [];

  // 表格数据
  partsList: any[] = [];

  // 左侧树相关
  treeProps = {
    children: 'children',
    label: 'menuName'
  };
  searchKeyword: string = '';
  filteredMenuList: any[] = [];
  expandedKeys: string[] = [];
  selectedMenu: any = null;

  // 弹窗控制
  dialogVisible: boolean = false;
  dialogType: "add" | "edit" = "add";
  showIconSelector: boolean = false;

  // 常用图标列表
  commonIcons: string[] = [
    'el-icon-menu', 'el-icon-setting', 'el-icon-user', 'el-icon-s-tools',
    'el-icon-s-home', 'el-icon-s-marketing', 'el-icon-s-platform', 'el-icon-s-cooperation',
    'el-icon-s-order', 'el-icon-s-data', 'el-icon-s-custom', 'el-icon-s-shop',
    'el-icon-document', 'el-icon-folder', 'el-icon-ticket', 'el-icon-message',
    'el-icon-collection', 'el-icon-s-grid', 'el-icon-s-management', 'el-icon-s-operation'
  ];

  // 表单数据
  currentForm: MenuForm = {
    menuName: "",
    menuIcon: "",
    resourceType: 2,
    pageUrl: "",
    displayOrder: 0,
    menuParent: "",
    menuParentName: "",
    menuCode: "",
    status: 1,
    resourceCode: "",
    parentResourceCode: "",
    parentMenuCode: "",
    accessType: "3",
    resourceClass: "",
  };
  globalResourceClass: string = "3";

  // 表单校验规则
  formRules = {
    menuName: [{ required: true, message: "请输入菜单名称", trigger: "blur" }],
    menuParent: [{ required: true, message: "请选择上级节点", trigger: "blur" }],
    menuCode: [{ required: true, message: "请输入菜单编码", trigger: "blur" }],
    resourceCode: [{ required: true, message: "请输入资源编码", trigger: "blur" }],
    displayOrder: [{ required: true, message: "请输入排序序号", trigger: "blur" }]
  };

  // 树形选择配置
  treeSelectProps = {
    value: "menuCode",
    label: "menuName",
    children: "children"
  };

  // 声明作用域插槽的类型
  $scopedSlots!: {
    default: (props: TableScope) => any;
  };

  created(): void {
    this.init();
  }

  async init(): Promise<void> {
    await this.loadListData();
  }

  // 监听搜索关键词变化
  @Watch('searchKeyword')
  onSearchKeywordChange(val: string) {
    this.$refs.menuTree && (this.$refs.menuTree as any).filter(val);
  }

  // 刷新页面
  handleRefresh(): void {
    this.loadListData();
  }

  // 加载列表数据
  @Table.LoadDataMethod()
  loadListData(): Promise<any> {
    return this.getMenuList({
      menuParent: this.getMenuParent(),
      pageNum: this.mix_pageNum,
      pageSize: 9999
    }).then((data: any) => {
      const processedData = this.processMenuData(data.data.list);
      this.partsList = processedData;
      this.filteredMenuList = processedData;

      // 默认展开第一级
      this.expandFirstLevel();

      return data;
    });
  }

  getMenuParent(){
    switch (this.globalResourceClass){
      case "1":
        return  "pc_root";
        break;
      case "2":
        return  "app_root";
        break;
      case "3":
        return  "op_root";
        break;
    }
    return "op_root";
  }

  // 默认展开第一级菜单
  expandFirstLevel(): void {
    if (this.partsList.length > 0) {
      this.expandedKeys = this.partsList.map((item: any) => item.id);
    }
  }

  // 过滤节点方法
  filterNode(value: string, data: any): boolean {
    if (!value) return true;
    return data.menuName.toLowerCase().includes(value.toLowerCase()) ||
           data.menuCode.toLowerCase().includes(value.toLowerCase()) ||
           data.resourceCode.toLowerCase().includes(value.toLowerCase());
  }

  // 处理搜索
  handleSearch(): void {
    if (this.$refs.menuTree) {
      (this.$refs.menuTree as any).filter(this.searchKeyword);
    }
  }

  // 处理树节点点击
  handleTreeNodeClick(data: any): void {
    this.selectedMenu = data;
  }

  // 获取父级菜单名称
  getParentMenuName(menuCode: string): string {
    const findParent = (list: any[], code: string): any => {
      for (const item of list) {
        if (item.menuCode === code) {
          return item.menuName;
        }
        if (item.children && item.children.length) {
          const found = findParent(item.children, code);
          if (found) return found;
        }
      }
      return '';
    };

    return findParent(this.partsList, menuCode) || menuCode;
  }

  // 选择图标
  selectIcon(icon: string): void {
    this.currentForm.menuIcon = icon;
    this.showIconSelector = false;
  }

  // 处理菜单数据，添加层级信息
  processMenuData(data: any[], level: number = 1): any[] {
    return data.map(item => {
      const processedItem = {
        ...item,
        level,
        expanded: false
      };

      // 确保子节点存在时递归处理，并正确递增层级
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        processedItem.children = this.processMenuData(item.children, level + 1);
      } else {
        processedItem.children = []; // 显式设置空数组避免层级继承问题
      }

      return processedItem;
    });
  }

  // 获取资源类型名称
  getResourceTypeName(row: any): string {
    switch (row.resourceType){
      case 1:
        return "应用";
        break;
      case 2:
        return "菜单";
        break;
      case 3:
        return "接口";
        break;
      case 4:
          return "按钮";
          break;
    }
  }

  // 处理新增
  handleAdd(row: any): void {
    this.dialogType = "add";
    this.resetForm();
    this.currentForm = {
      ...this.currentForm,
      menuParentName: row.menuName,
      parentResourceCode: row.resourceCode,
      parentMenuCode: row.menuCode,
      menuParent: row.menuCode,
      resourceClass: this.globalResourceClass
    };
    this.dialogVisible = true;
  }

  // 处理新增一级菜单
  handleAddParent(): void {
    this.dialogType = "add";
    this.resetForm();
    this.currentForm = {
      ...this.currentForm,
      menuParentName: this.getMenuParent(),
      parentResourceCode:this.getMenuParent(),
      parentMenuCode: this.getMenuParent(),
      menuParent: this.getMenuParent()
    };
    this.dialogVisible = true;
  }

  // 处理编辑
  handleEdit(row: any): void {
    this.dialogType = "edit";
    Promise.all([
      this.getMenuByCode({ menuCode: row.menuCode }),
      this.getMenuByCode({ menuCode: row.menuParent })
    ]).then(([data1, data2]) => {
      if (data1.code === "0") {
        this.currentForm = data1.data;
      }
      if (data2.code === "0") {
        this.currentForm.menuParentName = data2.data.menuName;
        this.currentForm.parentResourceCode = data2.data.resourceCode;
        this.currentForm.parentMenuCode = data2.data.menuCode;
        this.currentForm.menuParent = data2.data.menuCode;
        this.currentForm.resourceType = data2.data.resourceType;
      }
      this.dialogVisible = true;
    });
  }

  // 处理删除
  handleDel(row: any): void {
    this.$confirm("删除该菜单同时也会删除账号授予的菜单权限？", "删除提示", {
      confirmButtonText: "确认",
      cancelButtonText: "取消",
      type: "warning"
    }).then(() => {
      this.delMenuAndResource({
        id: row.id
      }).then((data: any) => {
        if (data.code == 0) {
          this.loadListData();
          this.resetForm();
          if (this.selectedMenu && this.selectedMenu.id === row.id) {
            this.selectedMenu = null;
          }
        }
      });
    });
  }

  // 处理节点点击
  handleNodeClick(data: any): void {
    this.currentForm.menuParent = data.menuCode;
    this.currentForm.menuParentName = data.menuName;
    this.currentForm.parentMenuCode = data.menuCode;
    this.currentForm.parentResourceCode = data.resourceCode;
  }

  // 处理表单提交
  handleSubmit(): void {
    (this.$refs.formRef as any).validate((valid: boolean) => {
      if (valid) {
        const submitData = {
          ...this.currentForm,
          resourceClass: this.globalResourceClass,
          status: 1
        };

        const action = this.dialogType === "add" ? this.saveMenuAndResource : this.updateMenuAndResource;

        action(submitData).then((data: any) => {
          if (data.code == 0) {
            this.loadListData();
            this.dialogVisible = false;
            this.resetForm();
          }
        });
      }
    });
  }

  // 重置表单
  resetForm(): void {
    this.currentForm = {
      menuName: "",
      menuIcon: "",
      resourceType:2,
      pageUrl: "",
      displayOrder: 0,
      menuParent: "",
      menuParentName: "",
      menuCode: "",
      status: 1,
      resourceCode: "",
      parentResourceCode: "",
      parentMenuCode: "",
      accessType: "3",
      resourceClass: "",
    };
    if (this.$refs.formRef) {
      (this.$refs.formRef as any).resetFields();
    }
  }

  // 处理表格选择
  mix_handleRowSelect(selection: any[]): void {
    this.mix_selectedRows = selection;
  }

  handleResourceClassChange(){
    this.loadListData();
  }
}
